(() => {
class ProductVariantOrderGrid extends HTMLElement {
  constructor() {
    super();

    this.rowsContainer = this.querySelector('[data-order-grid-rows]');
    this.rows = Array.from(this.querySelectorAll('[data-order-grid-row]'));
    this.swatches = Array.from(this.querySelectorAll('[data-order-grid-color]'));
    this.colorName = this.querySelector('[data-order-grid-color-name]');
    this.itemCount = this.querySelector('[data-order-grid-item-count]');
    this.subtotal = this.querySelector('[data-order-grid-subtotal]');
    this.submitButton = this.querySelector('[data-order-grid-submit]');
    this.removeAllButton = this.querySelector('[data-order-grid-remove-all]');
    this.removeAllWrap = this.querySelector('[data-order-grid-remove-all-wrap]');
    this.message = this.querySelector('[data-order-grid-message]');
    this.selectedColor = this.dataset.selectedColor || 'all';
    this.selectedSequence = 0;

    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onInput);
    this.submitButton?.addEventListener('click', this.onSubmit);
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('change', this.onInput);
    this.submitButton?.removeEventListener('click', this.onSubmit);
  }

  onClick(event) {
    const swatch = event.target.closest('[data-order-grid-color]');
    if (swatch) {
      this.selectColor(swatch);
      return;
    }

    if (event.target.closest('[data-order-grid-remove-all]')) {
      this.removeAll();
      return;
    }

    const row = event.target.closest('[data-order-grid-row]');
    if (!row) return;

    const input = row.querySelector('[data-order-grid-quantity]');
    if (!input) return;

    if (event.target.closest('[data-order-grid-plus]')) {
      this.changeQuantity(input, 1);
    } else if (event.target.closest('[data-order-grid-minus]')) {
      this.changeQuantity(input, -1);
    } else if (event.target.closest('[data-order-grid-remove]')) {
      input.value = 0;
      this.updateRowSelection(row, 0);
      this.render();
    }
  }

  onInput(event) {
    const input = event.target.closest('[data-order-grid-quantity]');
    if (!input) return;

    const row = input.closest('[data-order-grid-row]');
    const quantity = this.normalizeQuantity(input);
    input.value = quantity;
    this.updateRowSelection(row, quantity);
    this.render();
  }

  selectColor(swatch) {
    this.selectedColor = swatch.dataset.orderGridColor;

    this.swatches.forEach((item) => {
      const isActive = item === swatch;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (this.colorName) {
      this.colorName.textContent = swatch.dataset.orderGridColorLabel;
    }

    this.render();
  }

  changeQuantity(input, direction) {
    const current = this.toInteger(input.value);
    const step = Math.max(this.toInteger(input.step) || 1, 1);
    const positiveMin = Math.max(this.toInteger(input.dataset.positiveMin) || 1, 1);
    let next;

    if (direction > 0) {
      next = current <= 0 ? positiveMin : current + step;
    } else {
      next = current <= positiveMin ? 0 : current - step;
    }

    input.value = this.clampQuantity(input, next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  normalizeQuantity(input) {
    let quantity = this.toInteger(input.value);
    const step = Math.max(this.toInteger(input.step) || 1, 1);
    const positiveMin = Math.max(this.toInteger(input.dataset.positiveMin) || 1, 1);

    if (quantity <= 0) return 0;
    if (quantity < positiveMin) quantity = positiveMin;

    const stepOffset = quantity - positiveMin;
    if (stepOffset % step !== 0) {
      quantity = positiveMin + Math.round(stepOffset / step) * step;
    }

    return this.clampQuantity(input, quantity);
  }

  clampQuantity(input, quantity) {
    const max = input.max === '' ? Number.POSITIVE_INFINITY : this.toInteger(input.max);
    return Math.max(0, Math.min(quantity, max));
  }

  updateRowSelection(row, quantity) {
    if (quantity > 0 && !row.dataset.selectedOrder) {
      this.selectedSequence += 1;
      row.dataset.selectedOrder = String(this.selectedSequence);
    }

    if (quantity === 0) {
      delete row.dataset.selectedOrder;
    }
  }

  render() {
    this.sortAndFilterRows();

    let totalItems = 0;
    let subtotalCents = 0;

    this.rows.forEach((row) => {
      const input = row.querySelector('[data-order-grid-quantity]');
      const quantity = input ? this.toInteger(input.value) : 0;
      const price = this.toInteger(row.dataset.price);
      const lineTotal = quantity * price;
      const removeButton = row.querySelector('[data-order-grid-remove]');

      totalItems += quantity;
      subtotalCents += lineTotal;

      if (removeButton) {
        const isHidden = quantity <= 0;
        removeButton.classList.toggle('is-hidden', isHidden);
        removeButton.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
        removeButton.tabIndex = isHidden ? -1 : 0;
      }

      const lineTotalElement = row.querySelector('[data-order-grid-line-total]');
      if (lineTotalElement) {
        lineTotalElement.textContent = this.formatMoney(lineTotal);
      }
    });

    if (this.itemCount) this.itemCount.textContent = String(totalItems);
    if (this.subtotal) this.subtotal.textContent = this.formatMoney(subtotalCents);
    if (this.removeAllButton) this.removeAllButton.hidden = totalItems <= 0;
    if (this.removeAllWrap) this.removeAllWrap.hidden = totalItems <= 0;
    if (this.message) this.message.textContent = '';
  }

  removeAll() {
    this.rows.forEach((row) => {
      const input = row.querySelector('[data-order-grid-quantity]');
      if (input) input.value = 0;
      delete row.dataset.selectedOrder;
    });

    this.render();
  }

  sortAndFilterRows() {
    const orderedRows = [...this.rows].sort((rowA, rowB) => {
      const selectedA = this.toInteger(rowA.dataset.selectedOrder);
      const selectedB = this.toInteger(rowB.dataset.selectedOrder);
      const pinnedA = selectedA && rowA.dataset.color !== this.selectedColor;
      const pinnedB = selectedB && rowB.dataset.color !== this.selectedColor;

      if (pinnedA && pinnedB) return selectedA - selectedB;
      if (pinnedA) return -1;
      if (pinnedB) return 1;

      return this.toInteger(rowA.dataset.originalOrder) - this.toInteger(rowB.dataset.originalOrder);
    });

    const currentRows = Array.from(this.rowsContainer.children).filter((row) => this.rows.includes(row));
    const orderChanged = orderedRows.some((row, index) => currentRows[index] !== row);

    orderedRows.forEach((row) => {
      const input = row.querySelector('[data-order-grid-quantity]');
      const isSelected = input && this.toInteger(input.value) > 0;
      const isCurrentColor = this.selectedColor === 'all' || row.dataset.color === this.selectedColor;

      row.classList.toggle('is-hidden', !isSelected && !isCurrentColor);
    });

    if (orderChanged) orderedRows.forEach((row) => this.rowsContainer.appendChild(row));
  }

  getSelectedItems() {
    return this.rows.reduce((items, row) => {
      const input = row.querySelector('[data-order-grid-quantity]');
      const quantity = input ? this.normalizeQuantity(input) : 0;

      if (quantity > 0) {
        items.push({
          id: Number(row.dataset.variantId),
          quantity
        });
      }

      return items;
    }, []);
  }

  async onSubmit() {
    const items = this.getSelectedItems();

    if (!items.length) {
      this.showMessage(this.dataset.emptyMessage || 'Select at least one variant.');
      return;
    }

    this.setLoading(true);

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || result.message || 'Unable to add the selected variants.');
      }

      window.location.assign('/cart');
    } catch (error) {
      this.showMessage(error.message);
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.classList.toggle('is-loading', isLoading);
    this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  showMessage(message) {
    if (this.message) this.message.textContent = message;
  }

  formatMoney(cents) {
    let formattedMoney;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      formattedMoney = window.Shopify.formatMoney(cents, window.money_format || this.dataset.moneyFormat);
    } else {
      formattedMoney = (cents / 100).toFixed(2);
    }

    return String(formattedMoney)
      .replace(/<[^>]*>/g, '')
      .replace(/\s*USD\b/gi, '')
      .trim();
  }

  toInteger(value) {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) ? number : 0;
  }
}

if (!customElements.get('variant-order-grid')) {
  customElements.define('variant-order-grid', ProductVariantOrderGrid);
}
})();
